import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { SiteManagementService } from '../site-management.service';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { TranslateService } from '@ngx-translate/core';
import { ICardCredentials } from 'src/app/user-management/user-management-interfaces';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { SiteSettingsComponent } from '../site-settings/site-settings.component';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { NavigationManagerService } from 'src/app/shared/services/navigation-manager.service';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { BRANDS } from 'src/app/shared/models/screen-size.enum';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';

@Component({
  selector: 'app-master-credential',
  templateUrl: './master-credential.component.html',
  styleUrls: ['./master-credential.component.scss'],
})
export class MasterCredentialComponent implements OnInit, OnDestroy {
  sharedData: any;
  subscription: Subscription | undefined;
  masterCards: ICardCredentials[] = [];
  isLoading = false;
  readonly LANG_CONST = LANG_CONST;
  isWeiser = false;

  constructor(
    public dialogRef: MatDialogRef<MasterCredentialComponent>,
    private sharedDataService: SharedDataService,
    private siteManagementService: SiteManagementService,
    private notificationService: NotificationService,
    private localizationService: LocalizationService,
    private helperService: HelperService,
    private navigationManagerService: NavigationManagerService,
    private stateManagementService: StateManagementService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.localizationService.setupLocalization();
    this.isWeiser =
      this.stateManagementService?.getUserBrand() === BRANDS.WEISER;
    this.subscription = this.sharedDataService.currentData.subscribe(
      (sharedData) => {
        this.sharedData = sharedData;
        this.loadMasterCards();
      }
    );
  }

  loadMasterCards(): void {
    const siteId = this.sharedData?.selectedSite?.siteId;
    if (!siteId) {
      return;
    }
    this.isLoading = true;
    this.siteManagementService.getCardsBySite(siteId, 1).subscribe({
      next: (cards: ICardCredentials[]) => {
        if (cards && cards.length) {
          const mapped = cards.map((card: any, index: number) => {
            return {
              ...card,
              cardName: card.cardName
                ? `${this.translate.instant('MASTER')} ${card.cardName}`
                : `${this.translate.instant('MASTER')} Fob ${index + 1}`,
            };
          });
          this.masterCards = mapped;
        } else {
          this.masterCards = [];
        }
        this.isLoading = false;
      },
      error: (err: Error) => {
        this.isLoading = false;
        this.notificationService.showError(err?.message);
      },
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  navigateBack(): void {
    this.dialogRef.close();
    this.helperService.openModalDialog(
      SiteSettingsComponent,
      this.sharedData?.selectedSite,
      CONSTANTS.SITE_SETTINGS_MODAL
    );
  }

  openTermsLink(event: Event): void {
    event.preventDefault();
    const brand = this.stateManagementService?.getUserBrand();
    const lang = this.stateManagementService?.getUserLanguage();
    const url =
      brand === BRANDS.WEISER
        ? lang === LANG_CONST.FRENCH
          ? CONSTANTS.TERMS_OF_SERVICE_FRENCH_WEISER
          : CONSTANTS.TERMS_OF_SERVICE_WEISER
        : CONSTANTS.TERMS_OF_SERVICE;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  get masterCredentialTermsLinkKey(): string {
    return this.isWeiser
      ? 'MASTER_CREDENTIAL_TERMS_LINK_WEISER'
      : 'MASTER_CREDENTIAL_TERMS_LINK';
  }

  navigateToMasterCredentialDetail(card: ICardCredentials): void {
    const credentialData = {
      name: card.cardName || '',
      badgeId: card.badgeId || '',
      status: card.cardStatus || '',
      access: card.accessCount ? card.accessCount.toString() : 'All',
      description: card.description || '',
      assignTo: card.creatorName || card.credentialUser || '',
    };
    this.sharedDataService.setSelectedMasterCredential(credentialData);
    this.navigationManagerService.setStepSiteManager(
      CONSTANTS.MASTER_CREDENTIAL_DETAIL
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
